'use client';

import Image from 'next/image';
import { useTranslations } from 'next-intl';
import type React from 'react';
import { useEffect } from 'react';
import { Button } from '@/app/components/ui/button';
import { HOME_PAGE } from '@/app/constants';
import { reportEvent } from '@/app/utils';
import { useRouter } from '@/i18n/routing';
import { reportProductPageElementClick } from '../../_components/ProductInputTool/utils';
import { DemoSection, type DemoSectionType } from './DemoSection';
import { QuestionSection } from './QuestionSection';
import { renderRichListItem } from './richText';

export const ViduQ3VsSeedance2VsKling3: React.FC = () => {
  const router = useRouter();
  const t = useTranslations('vidu-q3-vs-seedance-2-0-vs-kling-3-0');

  useEffect(() => {
    reportEvent('feature_page_view', {
      from_page: 'vidu q3 vs seedance 2-0 vs kling 3-0',
    });
  }, []);

  const demos: DemoSectionType[] = [
    {
      id: 'demo1',
      title: 'demo.demo1.title',
      performanceAnalysis: 'demo.demo1.performanceAnalysis',
      performanceAnalysisContent: 'demo.demo1.performanceAnalysisContent',
      media: {
        vidu: {
          videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/vidu-1-bdbf3c3a.mp4',
          videoPoster: 'https://image01.cf.vidu.studio/vidu/media-asset/1-b725effe.webp',
        },
        seedance: {
          videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/seedance-1-ba2da1e8.mp4',
          videoPoster: 'https://image01.cf.vidu.studio/vidu/media-asset/1-220cea67.webp',
        },
        kling: {
          videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/keling-1-5694afc4.mp4',
          videoPoster: 'https://image01.cf.vidu.studio/vidu/media-asset/1-17ce5afe.webp',
        },
      },
      prompt: {
        text: `A 23-year-old American woman with delicate and charming features, natural makeup, clear skin, and expressive eyes. Cinematic close-up shot. Soft window light from the side. The camera slowly and gently tracks toward her face. She looks deeply heartbroken. Her breathing is uneven. Her lips tremble slightly before she speaks. Her voice is beautiful, clear, soft, and emotionally fragile, with a natural American accent. Slight vocal tremble and breathiness. She looks at the camera, eyes glossy, holding back tears, and says softly: Hey… are you okay? Her voice lowers and cracks slightly. I hate seeing you like this… it breaks my heart. Her eyes fill with tears. One tear rolls down naturally. She sniffles softly. Her voice shakes more as she continues: Come here… please.Let me hold you for a second. Her shoulders rise subtly with emotion. Her chest moves with uneven breathing. She gently wipes her tears with the back of her hand.
`,
        image: 'https://image01.cf.vidu.studio/vidu/media-asset/20260212-192118-d9b46084.webp',
      },
    },
    {
      id: 'demo2',
      title: 'demo.demo2.title',
      performanceAnalysis: 'demo.demo2.performanceAnalysis',
      performanceAnalysisContent: 'demo.demo2.performanceAnalysisContent',
      media: {
        vidu: {
          videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/vidu-2-c340fa3f.mp4',
          videoPoster: 'https://image01.cf.vidu.studio/vidu/media-asset/1-902bc08d.webp',
        },
        seedance: {
          videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/seedance-2-ebe2cc15.mp4',
          videoPoster: 'https://image01.cf.vidu.studio/vidu/media-asset/1-a21c7696.webp',
        },
        kling: {
          videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/keling-2-8ce5edd4.mp4',
          videoPoster: 'https://image01.cf.vidu.studio/vidu/media-asset/1-9b6faebc.webp',
        },
      },
      prompt: {
        text: 'In the first-person perspective, the camera starts from a high altitude and circles around the Frost God statue in 360 degrees.',
        image: 'https://image01.cf.vidu.studio/vidu/media-asset/20260212-192044-5532480f.webp',
      },
    },
    {
      id: 'demo3',
      title: 'demo.demo3.title',
      performanceAnalysis: 'demo.demo3.performanceAnalysis',
      performanceAnalysisContent: 'demo.demo3.performanceAnalysisContent',
      media: {
        vidu: {
          videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/vidu-3-00b0c5a4.mp4',
          videoPoster: 'https://image01.cf.vidu.studio/vidu/media-asset/1-55d669b8.webp',
        },
        seedance: {
          videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/seedance-3-8efc935f.mp4',
          videoPoster: 'https://image01.cf.vidu.studio/vidu/media-asset/1-878fa4d7.webp',
        },
        kling: {
          videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/keling-3-9d498fb8.mp4',
          videoPoster: 'https://image01.cf.vidu.studio/vidu/media-asset/1-96aebeb0.webp',
        },
      },
      prompt: {
        text: 'アニメ風、日本語音声、SFXあり、BGMなし\n女の子がカメラを見つめながら、ゆっくり歩み寄る。\nセリフ「よくやったなあ、悪い子の代償、わかってるだろう～」\n女の子がほうきを握り、カメラに向かって振り下ろす。\n画面が暗くなり、衝撃音が響く',
        image: 'https://image01.cf.vidu.studio/vidu/media-asset/prompt-01-0a5d4423.webp',
      },
    },
  ];

  // 处理生成按钮点击
  const handleGenerateClick = () => {
    reportEvent('element_click', {
      element_name: 'generate-now',
      page: 'vidu q3 vs seedance 2-0 vs kling 3-0',
    });
    reportProductPageElementClick('generate now');
    router.push(HOME_PAGE);
  };

  return (
    <div className="flex min-h-screen w-full flex-col bg-black text-white">
      <img
        src="https://image01.cf.vidu.studio/vidu/media-asset/vidu-q3-vs-seedance-2-0-vs-kling-3-0-73daa763.webp"
        alt="Vidu Q3 vs Seedance 2.0 vs Kling 3.0 comparison background"
        className="pointer-events-none absolute top-0 left-0 h-[659px] w-full"
      />
      {/* Hero Section */}
      <section className="relative pt-[208px] sm:pt-16">
        <div className="mx-auto flex flex-col items-center px-4">
          <div className="w-[58vw] text-center">
            <h1 className="mb-6 font-semibold text-[48px] leading-[100%] sm:text-[36px]">{t('title')}</h1>
            <p className="max-w-[52vw] font-normal text-2xl leading-[140%] sm:text-base">{t('description')}</p>
          </div>
          <Button
            className="mt-[104px] h-[68px] min-w-[272px] rounded-[100px] bg-[#0086FF] font-medium text-white text-xl shadow-lg transition-all hover:shadow-xl sm:h-12 sm:w-[195px] sm:py-4 sm:text-base"
            onClick={handleGenerateClick}
          >
            {t('buttons.generateNow')}
          </Button>
        </div>
      </section>

      <section className="relative mt-[56px] sm:mt-6">
        <div className="mx-auto w-[75vw] max-w-[1440px] rounded-[24px] bg-system-white08 px-8 py-6 font-normal text-white text-xl leading-[140%] sm:px-4 sm:py-3">
          {t.rich('introduction', {
            p: (chunks) => <p>{chunks}</p>,
            li: renderRichListItem,
          })}
          <h2 className="mt-4 mb-2 font-semibold">{t('dimensions.title')}</h2>
          <div className="leading-[160%]">
            {t.rich('dimensions.description', {
              p: (chunks) => <p>{chunks}</p>,
              li: renderRichListItem,
            })}
          </div>
        </div>
      </section>

      {demos.map((demo) => (
        <DemoSection key={demo.id} {...demo} />
      ))}

      <section className="relative mx-auto mt-[56px] w-[75vw] max-w-[1440px] sm:mt-6">
        <div className="rounded-[24px] bg-system-white08 px-8 py-6 text-base text-system-white64 leading-[140%] sm:px-4 sm:py-3">
          <h2 className="mb-2 font-semibold text-white text-xl">{t('performStrongly.title')}</h2>
          {t.rich('performStrongly.description', {
            p: (chunks) => <p>{chunks}</p>,
            li: renderRichListItem,
          })}
        </div>
      </section>

      <section className="relative mx-auto mt-[56px] w-[75vw] max-w-[1440px] sm:mt-6">
        <div className="rounded-[24px] bg-system-white08 px-8 py-6 text-base text-system-white64 leading-[140%] sm:px-4 sm:py-3">
          <h2 className="mb-2 font-semibold text-white text-xl">{t('mattersToCreators.title')}</h2>
          {t.rich('mattersToCreators.description', {
            p: (chunks) => <p>{chunks}</p>,
            li: renderRichListItem,
          })}
        </div>
      </section>

      {/* FAQ Section */}
      <QuestionSection />

      {/* CTA Banner */}
      <section className="relative mb-[160px] h-[364px] w-full sm:h-[219px]">
        <Image
          src="https://image01.cf.vidu.studio/vidu/media-asset/CTA_Banner-0d6559ab.webp"
          alt="CTA Banner"
          fill
          className="object-cover"
          sizes="100vw"
          loading="lazy"
        />

        {/* 内容覆盖层 */}
        <div className="absolute inset-0 z-10 flex items-center justify-center">
          <div className="mx-auto w-[75vw] max-w-[1440px] px-4 text-center sm:px-8">
            <h2 className="mb-10 font-bold text-[48px] leading-[100%] sm:font-semibold sm:text-[32px]">
              {t('cta.title')}
            </h2>
            <Button
              className="h-[60px] min-w-[224px] rounded-[100px] bg-[#0086FF] px-10 py-5 font-medium text-white text-xl shadow-lg transition-all hover:shadow-xl sm:h-12 sm:w-[195px] sm:py-4 sm:text-base"
              onClick={handleGenerateClick}
            >
              {t('cta.button')}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
